'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useCart } from '@/contexts/CartContext';
import { pagesApi, authApi } from '@/lib/api';
import { Page, PageCategory } from '@/types';

export default function Header() {
  const router = useRouter();
  const { getCartItemsCount } = useCart();
  const [topMenuPages, setTopMenuPages] = useState<Page[]>([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  useEffect(() => {
    loadTopMenu();
    checkAuth();
  }, []);

  const loadTopMenu = async () => {
    try {
      const response = await pagesApi.getPagesByCategory(PageCategory.TOP_MENU);
      setTopMenuPages(response.data);
    } catch (error) {
      console.error('Failed to load top menu:', error);
    }
  };

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('amam_token');
      if (token) {
        await authApi.getProfile();
        setIsAuthenticated(true);
      }
    } catch (error) {
      setIsAuthenticated(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('amam_token');
    localStorage.removeItem('amam_user');
    setIsAuthenticated(false);
    setUserMenuOpen(false);
    router.push('/');
  };

  const cartCount = getCartItemsCount();

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link href="/" className="text-2xl font-bold text-blue-600 hover:text-blue-700">
            AmAm
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            {/* Top Menu Links */}
            <Link href="/menu" className="text-gray-700 hover:text-blue-600 font-medium">
              תפריט
            </Link>
            {topMenuPages.map(page => (
              <Link
                key={page._id}
                href={`/pages/${page.slug}`}
                className="text-gray-700 hover:text-blue-600 font-medium"
              >
                {page.titleHe || page.title}
              </Link>
            ))}

            {/* Cart */}
            <Link
              href="/cart"
              className="relative text-gray-700 hover:text-blue-600"
              aria-label="סל קניות"
            >
              🛒
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Link>

            {/* User Menu */}
            {isAuthenticated ? (
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 text-gray-700 hover:text-blue-600 font-medium"
                >
                  👤 החשבון שלי
                </button>
                {userMenuOpen && (
                  <div className="absolute left-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-2 border">
                    <Link
                      href="/account"
                      className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      לוח בקרה
                    </Link>
                    <Link
                      href="/account/orders"
                      className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      רכישות קודמות
                    </Link>
                    <Link
                      href="/account/addresses"
                      className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      כתובות שלי
                    </Link>
                    <Link
                      href="/account/favorites"
                      className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      מועדפים שלי
                    </Link>
                    <Link
                      href="/account/profile"
                      className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      פרופיל
                    </Link>
                    <hr className="my-2" />
                    <button
                      onClick={handleLogout}
                      className="block w-full text-right px-4 py-2 text-red-600 hover:bg-gray-100"
                    >
                      התנתק
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <Link
                  href="/login"
                  className="text-gray-700 hover:text-blue-600 font-medium"
                >
                  התחבר
                </Link>
                <Link
                  href="/register"
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                >
                  הירשם
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden text-gray-700 hover:text-blue-600"
            aria-label="תפריט"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t py-4 space-y-2">
            <Link
              href="/menu"
              className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded"
              onClick={() => setMobileMenuOpen(false)}
            >
              תפריט
            </Link>
            {topMenuPages.map(page => (
              <Link
                key={page._id}
                href={`/pages/${page.slug}`}
                className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded"
                onClick={() => setMobileMenuOpen(false)}
              >
                {page.titleHe || page.title}
              </Link>
            ))}
            <Link
              href="/cart"
              className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded"
              onClick={() => setMobileMenuOpen(false)}
            >
              🛒 סל קניות {cartCount > 0 && `(${cartCount})`}
            </Link>
            <hr className="my-2" />
            {isAuthenticated ? (
              <>
                <Link
                  href="/account"
                  className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  החשבון שלי
                </Link>
                <Link
                  href="/account/orders"
                  className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  רכישות קודמות
                </Link>
                <Link
                  href="/account/addresses"
                  className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  כתובות שלי
                </Link>
                <Link
                  href="/account/favorites"
                  className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  מועדפים שלי
                </Link>
                <button
                  onClick={() => {
                    handleLogout();
                    setMobileMenuOpen(false);
                  }}
                  className="block w-full text-right px-4 py-2 text-red-600 hover:bg-gray-100 rounded"
                >
                  התנתק
                </button>
              </>
            ) : (
              <>
                <Link
                  href="/login"
                  className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  התחבר
                </Link>
                <Link
                  href="/register"
                  className="block px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded text-center"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  הירשם
                </Link>
              </>
            )}
          </div>
        )}
      </nav>
    </header>
  );
}
